import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"

// Dynamic, so the client bundle never loads the server-only modules.
const entryStore = async () => {
  const [{ assertAdminContext }, store] = await Promise.all([
    import("@/lib/supabase/auth"),
    import("@/admin/companies/portfolio-entry-store"),
  ])
  await assertAdminContext()
  return store
}

const entryInput = z.object({ portfolioEntryId: z.string().uuid() }).strict()

export const loadPortfolioEntries = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ companyId: z.string().uuid() }).strict().parse(data)
  )
  .handler(async ({ data }) =>
    (await entryStore()).readPortfolioEntries(data.companyId)
  )

export type PortfolioEntryRow = Awaited<
  ReturnType<typeof loadPortfolioEntries>
>[number]

export const addPortfolioEntry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        portfolioId: z.string().uuid(),
        companyId: z.string().uuid(),
        isMonitoring: z.boolean(),
      })
      .strict()
      .parse(data)
  )
  .handler(async ({ data }) => {
    await (await entryStore()).addPortfolioEntry(data)
  })

export const setPortfolioEntryMode = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    entryInput.extend({ isMonitoring: z.boolean() }).parse(data)
  )
  .handler(async ({ data }) => {
    await (
      await entryStore()
    ).setPortfolioEntryMode(data.portfolioEntryId, data.isMonitoring)
  })

export const offboardPortfolioEntry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => entryInput.parse(data))
  .handler(async ({ data }) => {
    await (await entryStore()).offboardPortfolioEntry(data.portfolioEntryId)
  })

export const refreshSnapshot = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => entryInput.parse(data))
  .handler(async ({ data }) => {
    await (await entryStore()).refreshSnapshot(data.portfolioEntryId)
  })

// The board's selected Companies that one Portfolio holds take one mode there.
export const setCompaniesModeInPortfolio = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        portfolioId: z.string().uuid(),
        companyIds: z.array(z.string().uuid()).min(1),
        isMonitoring: z.boolean(),
      })
      .strict()
      .parse(data)
  )
  .handler(async ({ data }) => (await entryStore()).setCompaniesMode(data))

// The board's selected Companies join one Portfolio, an existing one or one
// created here by name.
export const addCompaniesToPortfolio = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        companyIds: z.array(z.string().uuid()).min(1),
        portfolio: z.union([
          z.object({ id: z.string().uuid() }).strict(),
          z.object({ name: z.string().trim().min(1).max(100) }).strict(),
        ]),
        isMonitoring: z.boolean(),
      })
      .strict()
      .parse(data)
  )
  .handler(async ({ data }) => {
    const store = await entryStore()
    const portfolioId =
      "id" in data.portfolio
        ? data.portfolio.id
        : await store.createPortfolio(data.portfolio.name)
    await store.placeCompanies(data.companyIds, [
      { portfolioId, isMonitoring: data.isMonitoring },
    ])
  })
